import RefreshIcon from "@assets/icons/refresh-icon.svg";
import EmailSyncIcon from "@assets/icons/email-sync-icon.svg";

import { cn } from "@utils/tailwind.util";
import { SyncEmailScreenAction } from "@enums/screens.enum";
import { ImageSection } from "@components/Sections/ImageSection";
import { Paragraph } from "@ui/Content";
import { Button } from "@ui/Button";
import { H3 } from "@ui/Headings";
import { View } from "@ui/View";

interface Props {
  isPending: boolean;
  onSave: VoidFunction;
  onSubscribe: VoidFunction;
  currentAction: SyncEmailScreenAction;
  syncedEmailsCount: number;
  isThreadSubscribed: boolean;
}

export default function SyncToExistingThreadScreen({
  isPending,
  onSave,
  onSubscribe,
  currentAction,
  syncedEmailsCount,
  isThreadSubscribed,
}: Props) {
  const emails = syncedEmailsCount > 1 ? "emails" : "email";

  // When already subscribed, "save this email" becomes the only, primary action.
  let saveLabel = isThreadSubscribed ? "Save this email too" : "Save this email only";
  if (isPending) saveLabel = "Saving email";

  const saveButtonType = isThreadSubscribed && !isPending ? "PRIMARY" : "TRANSPARENT";

  let saveButtonClass: string;
  if (isThreadSubscribed) {
    saveButtonClass = isPending
      ? "bg-teal-100 text-white dark:bg-gray-700 dark:text-teal-100"
      : "bg-teal-500 text-white";
  } else {
    saveButtonClass = isPending
      ? "bg-gray-50 dark:bg-gray-700 dark:text-teal-100"
      : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80";
  }

  return (
    <>
      <ImageSection
        mainIcon={<EmailSyncIcon className="w-full h-full z-20 object-contain" />}
        overlayIcon={
          isPending ? (
            <RefreshIcon className={"stroke-white stroke-[1.76] animate-slowpin-reverse"} />
          ) : null
        }
        overlayIconBackground={isPending ? "bg-teal-500" : "bg-gray-450"}
      />

      <div className="flex flex-col gap-3 pt-2 pb-3 px-4 mt-2">
        <H3>{`This thread has ${syncedEmailsCount} ${emails} already saved to Meridian.`}</H3>
        <View.Condition if={!isThreadSubscribed}>
          <Paragraph>
            Subscribe from here to capture all future replies, or save only this email
          </Paragraph>
        </View.Condition>
      </div>

      <div className="flex flex-col items-center gap-[18px]">
        <View.Condition if={!isThreadSubscribed && currentAction !== SyncEmailScreenAction.Sync}>
          <Button
            className={cn(
              "min-w-[220px] h-8",
              isPending
                ? "bg-teal-100 text-white dark:bg-gray-700 dark:text-teal-100"
                : "bg-teal-500 text-white"
            )}
            buttonType={isPending ? "TRANSPARENT" : "PRIMARY"}
            onClick={onSubscribe}
            disabled={isPending}
          >
            {isPending ? "Subscribing" : "Subscribe to Following Emails"}
          </Button>
        </View.Condition>

        <View.Condition if={!isThreadSubscribed && !currentAction}>
          <span className="text-gray-950 dark:text-white text-sm font-[400] leading-none">or</span>
        </View.Condition>

        <View.Condition if={isThreadSubscribed || currentAction !== SyncEmailScreenAction.Subscribe}>
          <Button
            className={cn("min-w-[220px] h-8", saveButtonClass)}
            buttonType={saveButtonType}
            onClick={onSave}
            disabled={isPending}
          >
            {saveLabel}
          </Button>
        </View.Condition>
      </div>
    </>
  );
}
